import { Box, keyframes, alpha, type SxProps, type Theme } from '@mui/material';
import { getCrmPalette } from '../theme/brand';

export const GUSTAVO_AVATAR = 'https://imperium.s3.us-east-1.amazonaws.com/team/gustavo-ejecutivo.png';

// Aura "IA en vivo": anillo cónico que gira, glow que late y un punto en órbita.
const spin = keyframes`to { transform: rotate(360deg); }`;
const pulse = keyframes`0%,100% { opacity: .45; transform: scale(1); } 50% { opacity: .9; transform: scale(1.08); }`;

/**
 * Avatar de Gustavo con aura de IA (no toca la imagen, la enmarca).
 * @param size  diámetro total en px
 * @param ring  grosor del anillo de color
 */
export default function GustavoAura({
  size = 44, ring = 2, glow = true, src = GUSTAVO_AVATAR, alt = 'Gustavo', accent, sx,
}: { size?: number; ring?: number; glow?: boolean; src?: string; alt?: string; accent?: string; sx?: SxProps<Theme> }) {
  /**
   * El aura toma el color de la MARCA ACTIVA.
   *
   * Antes era un gradiente fijo (esmeralda hasta el 20/08/2026, después cian). Pero este
   * avatar vive en los dos lados —la web pública de Booster y el CRM— y en el CRM la marca
   * depende del tenant: oro en Imperium, el acento de Booster en el resto, y el color
   * propio de cada white-label. Un aura cian sobre el CRM dorado de Imperium se leía como
   * un componente prestado de otra app.
   *
   * `accent` permite forzarlo (la landing pública lo hace para no tomar el color de un
   * tenant logueado); sin él, sale de la paleta activa.
   */
  const P = getCrmPalette();
  const base  = accent ?? P.brand.golden;
  const claro = accent ? accent : P.brand.goldenLight;
  const medio = accent ? accent : P.brand.goldenMid;
  const grad = `conic-gradient(from 0deg, ${base}, ${claro}, ${medio}, ${base})`;
  const dot = size > 60 ? 7 : 5;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'inline-block', ...sx }}>
      {glow && (
        <Box aria-hidden sx={{
          position: 'absolute', inset: -size * 0.16, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(base, 0.45)}, ${alpha(medio, 0.25)} 45%, transparent 70%)`,
          filter: 'blur(6px)', animation: `${pulse} 3.2s ease-in-out infinite`, pointerEvents: 'none',
        }} />
      )}
      {/* anillo cónico giratorio */}
      <Box aria-hidden sx={{
        position: 'absolute', inset: 0, borderRadius: '50%', background: grad,
        animation: `${spin} 4.5s linear infinite`,
      }} />
      {/* punto en órbita */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, animation: `${spin} 4.5s linear infinite` }}>
        <Box sx={{
          position: 'absolute', top: -dot / 2, left: '50%', width: dot, height: dot, borderRadius: '50%',
          transform: 'translateX(-50%)', background: claro, boxShadow: `0 0 8px 1px ${claro}`,
        }} />
      </Box>
      {/* foto, dejando ver el anillo */}
      <Box component="img" src={src} alt={alt} sx={{
        position: 'absolute', inset: ring, width: `calc(100% - ${ring * 2}px)`, height: `calc(100% - ${ring * 2}px)`,
        borderRadius: '50%', objectFit: 'cover', display: 'block', border: '2px solid #0b0d12',
      }} />
    </Box>
  );
}
